import { Spin } from "antd";
import i18n from "i18next";
import type { FC } from "react";
import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router";

import { FormManager } from "../../../../../components/Form/FormManager";
import { useFormSubmitCompletion } from "../../../../../components/Form/hooks/useFormSubmitCompletion";
import { FormLayoutType } from "../../../../../components/Form/types";
import type { FormProps } from "../../../../../components/Form/types/FormProps";
import { ResourceName } from "../../../../../core/router/config/ResourceName";
import { BASE_PATH, FORM_ACTION } from "../../../../../core/router/types/routerTypes";
import { showWarningNotification } from "../../../../../core/setup/redux/state/notifications/notificationsState";
import type { FormJson } from "../../../../../core/types/FormJsonTypes";
import { useCampaignSaveAndGenerate } from "../../hooks/useCampaignSaveAndGenerate";
import { useCampaignLoadQuery, useCampaignUpdateMutation } from "../../services/server/CampaignApi";
import type { CampaignEntity } from "../../types/CampaignEntity";
import campaignEditJson from "./campaign-orgunits-edit.json";

export const CampaignOrgUnitsEditForm: FC<FormProps> = ({ runSubmit, formAction }) => {
    const { id } = useParams();

    const formDefinition = campaignEditJson as FormJson;

    const [formData, setFormData] = useState<CampaignEntity>({} as CampaignEntity);
    const { data: loadFormData, isFetching: loadFormIsInProgress } = useCampaignLoadQuery({ entityId: id });
    useEffect(() => {
        if (loadFormData) {
            setFormData(loadFormData);
        }
    }, [loadFormData, setFormData]);
    const onChangeCallback = useCallback(setFormData, [setFormData]);

    const { saveAndGenerate, isSaveInProgress } = useCampaignSaveAndGenerate(useCampaignUpdateMutation);
    const dispatch = useDispatch();
    const completeSubmit = useFormSubmitCompletion(formAction, `${BASE_PATH}/${ResourceName.CAMPAIGNS}`);
    const onValidatedCallback = useCallback(
        async (valid: boolean) => {
            if (!valid || !formData) {
                return;
            }

            // is at least one terminal selected?
            const selectedTerminalIds = formData.terminals1;
            const selectedTerminalIds2 = formData.terminals2;
            if (!selectedTerminalIds || selectedTerminalIds.length === 0) {
                dispatch(
                    showWarningNotification({
                        text: i18n.t("campaign.error.required"),
                        autoCloseDisabled: true,
                    }),
                );
                return;
            }
            if (formData.terminals2 && (!selectedTerminalIds2 || selectedTerminalIds2.length === 0)) {
                dispatch(
                    showWarningNotification({
                        text: i18n.t("campaign.error.required"),
                        autoCloseDisabled: true,
                    }),
                );
                return;
            }

            const result = await saveAndGenerate(formData, formAction);

            const hasError = "error" in result;
            if (!hasError) {
                const successText =
                    formAction === FORM_ACTION.GENERATE
                        ? i18n.t("success.campaign.generate.successfully", { 0: result.data.id })
                        : i18n.t("common.formUpdateSuccess", { id: result.data.id, resource: "Campaign" });
                completeSubmit(successText);
            }
        },
        [saveAndGenerate, formData, formAction, completeSubmit, dispatch],
    );

    return (
        <Spin spinning={isSaveInProgress || loadFormIsInProgress}>
            {formDefinition?.formConfiguration && (
                <FormManager<CampaignEntity>
                    name="CampaignEditForm"
                    formData={formData}
                    formLayout={FormLayoutType.TABS}
                    formDefinition={formDefinition.formConfiguration}
                    onChange={onChangeCallback}
                    shouldValidate={runSubmit}
                    onValidated={onValidatedCallback}
                />
            )}
        </Spin>
    );
};
